/**
 * Archive Pagination
 *
 * @module
 */

import { Pagination as GenericPagination } from '../reusable/Pagination.js';
import { FOOL_GLOBALS } from '../../utilities/WindowGlobals.js';

export class Pagination {
    /**
     * JS pagination of archive posts listing via WP Rest API.
     *
     * @param {Element} buttonsContainer
     */
    constructor(buttonsContainer) {
        /** @type {HTMLElement} */
        // @ts-ignore: cannot easily cast to HTMLElement
        this.buttonsContainer = buttonsContainer;
    }

    /**
     * Extract the page number referenced by the page URL.
     *
     * @private
     * @param {string} href
     * @return {number}
     */
    getPageNumFromHref(href) {
        const pageNumMatchers = [
            href.match(/\/page\/([0-9]+)\//),
            href.match(/paged=([0-9]+)/),
        ];
        for (const matcher of pageNumMatchers) {
            if (matcher) {
                return parseInt(matcher[1], 10);
            }
        }
        return 1;
    }

    /**
     * Find paginated posts listings in the document.
     *
     * @private
     * @return {void}
     */
    findPaginatedListing() {
        /** @type {HTMLElement} */
        this.listing = document.querySelector('.articles-listing[data-has-pagination="true"][data-query]');
    }

    /**
     * Updated WP query arguments with the given page number.
     *
     * @private
     * @param {number} pageNum
     * @return {string} WP post query arguments as a query string.
     */
    createPagedQuery(pageNum) {
        const fetchArgs = new URLSearchParams(this.listing.getAttribute('data-query'));
        fetchArgs.set('paged', String(pageNum));
        return fetchArgs.toString();
    }

    /**
     * Display a loading spinner when fetching a new page.
     *
     * @private
     * @return {void}
     */
    beginLoadingTransition() {
        this.listing.style.height = this.listing.offsetHeight + 'px';
        this.listing.insertAdjacentHTML('beforeend', '<div class="loading-spinner"></div>');
        this.buttonsContainer.classList.add('busy');
        setTimeout(() => {
            this.listing.scrollIntoView({
                behavior: 'auto',
                block: 'start'
            });
            this.listing.classList.add('loading-listing');
        }, 64);
    }

    /**
     * Hide the loading spinner when fetching the page is complete.
     *
     * @private
     * @return {void}
     */
    endLoadingTransition() {
        this.listing.querySelector('.loading-spinner').remove();
        this.listing.classList.remove('loading-listing');
        this.buttonsContainer.classList.remove('busy');
        this.listing.style.height = '';
    }

    /**
     * Replace the old posts listing with the HTML from the new page.
     *
     * @private
     * @param {string} html
     * @return {void}
     */
    replaceListing(html) {
        this.listing.querySelector('main').remove();
        this.listing.insertAdjacentHTML('beforeend', html);
    }

    /**
     * Fetch post listing data for the specified page.
     *
     * @private
     * @param {number} pageNum
     * @return {Promise}
     */
    fetchListing(pageNum) {
        const apiUrl = new URL(FOOL_GLOBALS.FOOL_FREESITE.postsListingApiUrl);
        apiUrl.searchParams.append('fetchArgs', this.createPagedQuery(pageNum));
        return fetch(apiUrl.href);
    }

    /**
     * Replace the old pagination buttons with ones matching the selected page.
     *
     * @private
     * @param {string} html
     * @return {void}
     */
    replacePagination(html) {
        const div = document.createElement('div');
        this.buttonsContainer.innerHTML = '';
        div.innerHTML = html;

        setTimeout(() => {
            Array.from(div.querySelector('.pagination').children).forEach(btn => this.buttonsContainer.appendChild(btn));
            (new GenericPagination(this.buttonsContainer)).init();
        }, 16);
    }

    /**
     * Fetch the pagination buttons for the specified page.
     *
     * @private
     * @param {number} pageNum
     * @return {Promise}
     */
    fetchPagination(pageNum) {
        const apiUrl = new URL(FOOL_GLOBALS.FOOL_FREESITE.paginationApiUrl);

        apiUrl.searchParams.append('baseUrl', this.buttonsContainer.getAttribute('data-base-url'));
        apiUrl.searchParams.append('pageCount', this.buttonsContainer.getAttribute('data-page-count'));
        apiUrl.searchParams.append('currentPageNum', String(pageNum));

        return fetch(apiUrl.href);
    }

    /**
     * Replace the current history entry so the user can return to the initial page via the back button.
     *
     * @private
     * @return {void}
     */
    updateCurrentHistoryState() {
        history.replaceState(
            {
                pageNum: parseInt(this.buttonsContainer.getAttribute('data-current-page'), 10)
            },
            document.title
        );
    }

    /**
     * Update the browser location and add a new history entry when the user changes to a new page.
     *
     * @private
     * @param {string} url
     * @param {number} pageNum
     * @return {void}
     */
    pushHistoryState(url, pageNum) {
        history.pushState({ pageNum: pageNum }, document.title, url);
    }

    /**
     * Fetch the correct page when the user uses the history buttons.
     *
     * @private
     * @param {PopStateEvent} e
     * @return {void}
     */
    onHistoryStatePop(e) {
        if (e.state && ('pageNum' in e.state)) {
            this.goToPage(e.state.pageNum);
        }
    }

    /**
     * Update the post listing and pagination to match the output from the given page URL.
     *
     * @private
     * @param {string} url
     * @return {Promise}
     */
    goToPageFromUrl(url) {
        return this.goToPage(this.getPageNumFromHref(url));
    }

    /**
     * Update the post listing and pagination to match the output from the given page.
     *
     * @private
     * @param {number} pageNum
     * @return {Promise}
     */
    goToPage(pageNum) {
        if ((typeof pageNum !== 'number') || isNaN(pageNum) || pageNum < 1) {
            throw new TypeError('Invalid page number.');
        }

        this.beginLoadingTransition();

        return Promise.all([
            this.fetchListing(pageNum).then(resp => resp.text()).then(html => this.replaceListing(html)),
            this.fetchPagination(pageNum).then(resp => resp.text()).then(html => this.replacePagination(html)),
        ]).then(() => {
            setTimeout(() => {
                this.endLoadingTransition();
            }, 64);
            return Promise.resolve(pageNum);
        });
    }

    /**
     * Update the post listing and pagination to match the output from the clicked page button.
     *
     * @private
     * @param {MouseEvent} e
     * @return {void}
     */
    onClick(e) {
        e.preventDefault();
        /** @type {HTMLAnchorElement} */
        // @ts-ignore: do not understand the error
        const target = e.target;
        if (target.nodeName === 'A' && !target.classList.contains('current')) {
            const url = target.href;
            this.updateCurrentHistoryState();
            this.goToPageFromUrl(url).then(pageNum => this.pushHistoryState(url, pageNum));
        }
    }

    /**
     * Initialize the JS pagination features.
     *
     * 1. Change page on browser history changes.
     * 2. Change page on pagination button click.
     *
     * @return {void}
     */
    init() {
        this.findPaginatedListing();
        if (!this.listing) {
            return;
        }
        window.onpopstate = this.onHistoryStatePop.bind(this);
        this.buttonsContainer.addEventListener('click', e => this.onClick(e));
    }
}
